import type { CompletedRun, RenderedMessage } from "@/stores/testingStore";

/**
 * Pure layout helpers for a Testing chat session.
 *
 * Each plan has its own session (`plan:<id>`); within it, a proposed plan stays as an older message,
 * is independently runnable, and its run result cards render directly under ITS plan card — never at
 * the end and never under a different plan. Kept pure so the exact bugs the user hit (a pass card
 * creeping to the bottom / under the wrong plan, an older plan's card disappearing, results lost on
 * tab switch) are covered by unit tests without a browser.
 */

/**
 * The index of the LAST chat message carrying each planId's proposal card. A plan can appear more than
 * once (proposed, then updated → same id) — its run cards anchor under the most-recent occurrence so
 * they sit with the plan's current spec. Returns a plain object keyed by planId → message index.
 */
export function lastPlanMessageIndexByPlanId(
	messages: RenderedMessage[],
): Record<string, number> {
	const byId: Record<string, number> = {};
	messages.forEach((m, i) => {
		if (m.proposedPlan) byId[m.proposedPlan.id] = i;
	});
	return byId;
}

/** Whether a run result card should render right after the message at `index`. True only for the
 *  message that is the LAST occurrence of its plan — so a plan proposed twice doesn't double-render. */
export function isRunAnchorMessage(
	message: RenderedMessage,
	index: number,
	anchors: Record<string, number>,
): boolean {
	const plan = message.proposedPlan;
	return plan != null && anchors[plan.id] === index;
}

/** All completed runs for a plan, kept in chat order (oldest → newest) so multiple runs stack under
 *  the plan with the newest last (the newest is the only one that shows "what to test next"). */
export function runsForPlan(
	runs: CompletedRun[],
	planId: string,
): CompletedRun[] {
	return runs.filter((r) => r.planId === planId);
}

/**
 * One question-and-answer exchange: the user's message plus everything the agent produced in reply.
 *
 * The chat used to render `messages.map(...)` as one flat stream of cards separated by 6px, which is
 * why a session read as a wall of boxes with no sense of "you asked X, here is the answer". Grouping
 * into turns is what lets the view give each exchange its own block, divider and reading measure —
 * the structure Weave's thread already has.
 *
 * `assistant` is a list, not a single message: a run can append an extra assistant-only message to a
 * turn afterwards (see `postRepairPrompt`), and a leading turn can exist with no user message at all
 * (a session restored mid-stream).
 */
export interface ChatTurn {
	/** Stable React key — the user message's id, or the first assistant message's for a leading turn. */
	id: string;
	user: RenderedMessage | null;
	assistant: RenderedMessage[];
	/** Index of this turn's first message in the flat list — needed to resolve run anchors. */
	startIndex: number;
}

/**
 * Groups the flat message list into turns. A new turn begins at every `user` message; anything else
 * attaches to the turn in progress. Pure and order-preserving, so the rendered thread is always the
 * conversation in the order it happened.
 */
export function groupMessagesIntoTurns(messages: RenderedMessage[]): ChatTurn[] {
	const turns: ChatTurn[] = [];
	let current: ChatTurn | null = null;

	messages.forEach((message, index) => {
		if (message.role === "user") {
			current = { id: message.id, user: message, assistant: [], startIndex: index };
			turns.push(current);
			return;
		}
		if (!current) {
			current = { id: message.id, user: null, assistant: [], startIndex: index };
			turns.push(current);
		}
		current.assistant.push(message);
	});

	return turns;
}
